import React, { useState, useEffect, useCallback } from 'react';
import {
    Activity, RefreshCw, AlertTriangle, CheckCircle,
    XCircle, Database, RotateCcw, Loader,
    Play, Timer, ChevronDown, ChevronUp
} from 'lucide-react';
import LiveMonitor from '../LiveMonitor';

const themeBlue  = '#3b82f6';
const themeRed   = '#e11d48';
const themeOrange= '#f59e0b';
const themeGray  = '#64748b';

// ---------------------------------------------------------------------------
// UTILITÁRIOS (módulo — disponíveis para todos os sub-componentes)
// ---------------------------------------------------------------------------

// MySQL DATE pode voltar como "2026-05-04T03:00:00.000Z"
const formatPeriodo = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

// Códigos de processo para o hash
const JOB_CODES = {
    'ETL_FLEX_SALES':      'FS',
    'ETL_SALE_BY_PAYMENT': 'SBP',
    'ETL_SOVOS':           'SOV',
    'ETL_IFOOD':           'IFD',
};

/**
 * Gera o código padronizado da execução.
 * Formato: {seq_do_dia}{DDMMYYYY}{JOB}{TIPO}
 * Exemplo: 113052026FSA = 1ª execução do dia 13/05/2026, Flex Sales, Automático
 */
const gerarCodigo = (log, allLogs) => {
    if (!log.data_inicio) return `#${log.id}`;
    const d = new Date(log.data_inicio);
    const dia  = String(d.getDate()).padStart(2, '0');
    const mes  = String(d.getMonth() + 1).padStart(2, '0');
    const ano  = d.getFullYear();
    const data = `${dia}${mes}${ano}`;
    const jobCode  = JOB_CODES[log.job_name] || log.job_name.replace(/[^A-Za-z]/g,'').slice(0, 3).toUpperCase();
    const tipoCode = log.tipo_execucao === 'automatico' ? 'A' : 'R';
    // SEQ por dia — reseta a cada dia para o mesmo job+tipo
    const mesmoDiaTipo = allLogs
        .filter(l =>
            l.job_name      === log.job_name &&
            l.tipo_execucao === log.tipo_execucao &&
            l.data_inicio?.substring(0, 10) === log.data_inicio?.substring(0, 10)
        )
        .sort((a, b) => a.id - b.id);
    const seq = String(mesmoDiaTipo.findIndex(l => l.id === log.id) + 1).padStart(2, '0');
    return `${data}${jobCode}${tipoCode}${seq}`;
};

// ---------------------------------------------------------------------------
// STATUS BADGE
// ---------------------------------------------------------------------------
const StatusBadge = ({ status }) => {
    const cfg = {
        sucesso:        { bg: '#ecfdf5', color: '#059669', label: 'Sucesso',        Icon: CheckCircle  },
        parcial:        { bg: '#fffbeb', color: '#d97706', label: 'Parcial',        Icon: AlertTriangle },
        erro:           { bg: '#fff1f2', color: '#e11d48', label: 'Erro',           Icon: XCircle      },
        rodando:        { bg: '#eff6ff', color: '#3b82f6', label: 'Rodando...',     Icon: Loader       },
        reprocessamento:{ bg: '#f5f3ff', color: '#8b5cf6', label: 'Reprocessamento',Icon: RotateCcw    },
    }[status] || { bg: '#f1f5f9', color: '#64748b', label: status, Icon: Activity };

    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '4px 10px', borderRadius: '20px',
            backgroundColor: cfg.bg, color: cfg.color,
            fontSize: '12px', fontWeight: '700',
        }}>
            <cfg.Icon size={12} className={status === 'rodando' ? 'animate-spin' : ''} />
            {cfg.label}
        </span>
    );
};

// ---------------------------------------------------------------------------
// STAT CARD
// ---------------------------------------------------------------------------
const StatCard = ({ title, value, sub, icon: Icon, color, bgColor }) => (
    <div style={{
        backgroundColor: '#fff', padding: '20px', borderRadius: '16px',
        border: '1px solid #e2e8f0', borderLeft: `4px solid ${color}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
    }}>
        <div>
            <p style={{ color: '#64748b', fontSize: '12px', textTransform: 'uppercase', fontWeight: '800', margin: '0 0 4px 0', letterSpacing: '0.05em' }}>{title}</p>
            <p style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', margin: '0 0 4px 0' }}>{value}</p>
            <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0, fontWeight: '500' }}>{sub}</p>
        </div>
        <div style={{ padding: '16px', borderRadius: '50%', backgroundColor: bgColor, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon size={28} />
        </div>
    </div>
);

// ---------------------------------------------------------------------------
// MODAL DE REPROCESSAMENTO
// ---------------------------------------------------------------------------
const ModalReprocessar = ({ run, onClose, onConfirm, loading, resultado }) => {
    const detalhes = run?.detalhes ? (typeof run.detalhes === 'string' ? JSON.parse(run.detalhes) : run.detalhes) : {};
    const lojasFalhas = detalhes.lojas_com_erro?.join(', ') || 'todas';

    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '32px', maxWidth: '480px', width: '90%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                    <div style={{ padding: '10px', backgroundColor: '#eff6ff', borderRadius: '10px', color: themeBlue }}>
                        <RotateCcw size={22} />
                    </div>
                    <div>
                        <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', margin: 0 }}>Reprocessar Execução</h3>
                        <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>Reextrai e recarrega os dados que falharam</p>
                    </div>
                </div>

                {resultado ? (
                    <div style={{ padding: '16px', borderRadius: '10px', backgroundColor: resultado.ok ? '#ecfdf5' : '#fff1f2', border: `1px solid ${resultado.ok ? '#6ee7b7' : '#fecdd3'}`, marginBottom: '20px' }}>
                        <p style={{ color: resultado.ok ? '#059669' : themeRed, fontWeight: '700', margin: 0 }}>
                            {resultado.ok ? `✅ Iniciado! Log #${resultado.log_id} criado. Acompanhe na tabela.` : `❌ ${resultado.message}`}
                        </p>
                    </div>
                ) : (
                    <div style={{ backgroundColor: '#f8fafc', borderRadius: '10px', padding: '16px', marginBottom: '20px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div>
                                <p style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '700', margin: '0 0 4px 0' }}>Período</p>
                                <p style={{ fontSize: '14px', fontWeight: '700', color: '#334155', margin: 0 }}>
                                    {formatPeriodo(run?.periodo_inicio)}
                                    {run?.periodo_inicio !== run?.periodo_fim && run?.periodo_fim && ` → ${formatPeriodo(run?.periodo_fim)}`}
                                </p>
                            </div>
                            <div>
                                <p style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '700', margin: '0 0 4px 0' }}>Status original</p>
                                <StatusBadge status={run?.status} />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <p style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '700', margin: '0 0 4px 0' }}>Lojas a reprocessar</p>
                                <p style={{ fontSize: '14px', fontWeight: '700', color: themeRed, margin: 0 }}>{lojasFalhas}</p>
                            </div>
                        </div>
                    </div>
                )}

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', color: '#475569', fontWeight: '600', cursor: 'pointer' }}>
                        {resultado ? 'Fechar' : 'Cancelar'}
                    </button>
                    {!resultado && (
                        <button
                            onClick={() => onConfirm(run)}
                            disabled={loading}
                            style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', backgroundColor: themeBlue, color: '#fff', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', opacity: loading ? 0.7 : 1 }}
                        >
                            {loading ? <Loader size={16} className="animate-spin" /> : <Play size={16} />}
                            {loading ? 'Iniciando...' : 'Confirmar Reprocessamento'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// COMPONENTE PRINCIPAL
// ---------------------------------------------------------------------------
const AdminMonitoramento = () => {
    const [logs, setLogs]         = useState([]);
    const [stats, setStats]       = useState(null);
    const [loading, setLoading]   = useState(true);
    const [runModal, setRunModal]     = useState(null);
    const [reprocessLoading, setReprocessLoading] = useState(false);
    const [reprocessResult, setReprocessResult]   = useState(null);

    // Filtros
    const [filtroProcesso, setFiltroProcesso] = useState('');
    const [filtroStatus,   setFiltroStatus]   = useState('');
    const [filtroDataDe,   setFiltroDataDe]   = useState('');
    const [filtroDataAte,  setFiltroDataAte]  = useState('');

    // Paginação
    const POR_PAGINA = 15;
    const [pagina, setPagina] = useState(1);

    // Expand
    const [expandedId, setExpandedId] = useState(null);

    const token = localStorage.getItem('authToken');
    const headers = { 'Authorization': `Bearer ${token}` };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [resLogs, resStats] = await Promise.all([
                fetch('/api/monitoramento/logs',  { headers }),
                fetch('/api/monitoramento/stats', { headers }),
            ]);
            if (resLogs.ok)  setLogs(await resLogs.json());
            if (resStats.ok) setStats(await resStats.json());
        } catch (e) {
            console.error('Erro ao carregar monitoramento', e);
        } finally {
            setLoading(false);
        }
    }, []); // eslint-disable-line

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Auto-refresh: 10s se houver job rodando, 30s quando tudo parado
    useEffect(() => {
        const temJobRodando = logs.some(l => l.status === 'rodando');
        const intervalo = temJobRodando ? 10000 : 30000;
        const timer = setInterval(fetchData, intervalo);
        return () => clearInterval(timer);
    }, [logs, fetchData]); // eslint-disable-line

    const formatDuracao = (s) => {
        if (!s) return '-';
        if (s < 60)  return `${s}s`;
        if (s < 3600) return `${Math.floor(s/60)}m ${s%60}s`;
        return `${Math.floor(s/3600)}h ${Math.floor((s%3600)/60)}m`;
    };

    const formatData = (d) => {
        if (!d) return '-';
        return new Date(d).toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    // formatPeriodo está no módulo (topo do arquivo)

    const handleReprocessar = async (run) => {
        setReprocessLoading(true);
        try {
            const body = run.id_execucao_origem
                ? { reprocess_from: run.id }
                : { reprocess_from: run.id };

            const res = await fetch('/api/monitoramento/reprocessar', {
                method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await res.json();
            setReprocessResult({ ok: res.ok, ...data });
            if (res.ok) setTimeout(fetchData, 4000);
        } catch (e) {
            setReprocessResult({ ok: false, message: 'Erro de conexão.' });
        } finally {
            setReprocessLoading(false);
        }
    };

    // logsArr precisa vir ANTES de qualquer uso (evita TDZ)
    const logsArr         = logs || [];
    const processosUnicos = [...new Set(logsArr.map(l => l.job_name).filter(Boolean))];

    // IDs de runs que já têm um reprocessamento bem-sucedido vinculado
    const jaReprocessados = new Set(
        logsArr
            .filter(l => l.tipo_execucao === 'reprocessamento' && l.status === 'sucesso' && l.id_execucao_origem)
            .map(l => Number(l.id_execucao_origem))
    );

    const podeReprocessar  = (log) => ['parcial', 'erro'].includes(log.status) && log.tipo_execucao === 'automatico' && !jaReprocessados.has(log.id);
    const foiReprocessado  = (log) => ['parcial', 'erro'].includes(log.status) && log.tipo_execucao === 'automatico' &&  jaReprocessados.has(log.id);

    const limparFiltros = () => {
        setFiltroProcesso('');
        setFiltroStatus('');
        setFiltroDataDe('');
        setFiltroDataAte('');
        setPagina(1);
    };

    // Reset página quando filtros mudam
    useEffect(() => setPagina(1), [filtroProcesso, filtroStatus, filtroDataDe, filtroDataAte]);

    // Ordenação
    const [sortField, setSortField]   = useState('data_inicio');
    const [sortDir,   setSortDir]     = useState('desc');

    const toggleSort = (field) => {
        if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDir('desc'); }
    };

    const SortBtn = ({ field, label }) => {
        const ativo = sortField === field;
        return (
            <span
                onClick={() => toggleSort(field)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', userSelect: 'none', color: ativo ? '#0f172a' : '#64748b', fontWeight: ativo ? '800' : '600' }}
            >
                {label}
                <span style={{ fontSize: '10px', color: ativo ? '#3b82f6' : '#cbd5e1' }}>
                    {ativo ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
                </span>
            </span>
        );
    };

    const logsFiltrados = logsArr
        .filter(log => {
            if (filtroProcesso && log.job_name !== filtroProcesso) return false;
            if (filtroStatus   && log.status   !== filtroStatus)   return false;
            if (filtroDataDe   && log.periodo_inicio < filtroDataDe) return false;
            if (filtroDataAte  && log.periodo_fim    > filtroDataAte) return false;
            return true;
        })
        .sort((a, b) => {
            let va = a[sortField], vb = b[sortField];
            if (va === null || va === undefined) va = '';
            if (vb === null || vb === undefined) vb = '';
            if (typeof va === 'string') va = va.toLowerCase();
            if (typeof vb === 'string') vb = vb.toLowerCase();
            if (va < vb) return sortDir === 'asc' ? -1 : 1;
            if (va > vb) return sortDir === 'asc' ?  1 : -1;
            return 0;
        });

    const temFiltroAtivo  = filtroProcesso || filtroStatus || filtroDataDe || filtroDataAte;
    const totalPaginas    = Math.max(1, Math.ceil(logsFiltrados.length / POR_PAGINA));
    const logsVisiveis    = logsFiltrados.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

    const inputStyle = {
        padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0',
        fontSize: '13px', color: '#334155', outline: 'none',
        backgroundColor: '#fff', cursor: 'pointer',
    };

    const pgBtnStyle = (disabled) => ({
        padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0',
        backgroundColor: '#fff', color: disabled ? '#cbd5e1' : '#334155',
        fontWeight: '600', fontSize: '13px', cursor: disabled ? 'default' : 'pointer',
    });

    return (
        <div>
            {/* CABEÇALHO */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{ padding: '12px', backgroundColor: '#eff6ff', borderRadius: '12px', color: themeBlue }}>
                        <Database size={28} />
                    </div>
                    <div>
                        <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '0 0 4px 0' }}>Monitor de Integrações</h2>
                        <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>Saúde e histórico de execuções dos ETLs automáticos.</p>
                    </div>
                </div>
                <button
                    onClick={fetchData}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e2e8f0'}
                    onMouseOut={(e)  => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                >
                    <RefreshCw size={16} className={loading ? 'animate-spin' : ''} color={themeBlue} />
                    Atualizar Dados
                </button>
            </div>

            {/* STAT CARDS */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                <StatCard
                    title="Incidentes Abertos"
                    value={stats?.incidentes_abertos ?? '-'}
                    sub="Sem reprocessamento nos últimos 7 dias"
                    icon={AlertTriangle}
                    color={themeOrange}
                    bgColor="#fffbeb"
                />
                <StatCard
                    title="Média Automático"
                    value={stats ? formatDuracao(Math.round(stats.avg_duracao_automatico ?? 0)) : '-'}
                    sub="Execuções automáticas com sucesso"
                    icon={Timer}
                    color={themeBlue}
                    bgColor="#eff6ff"
                />
                <StatCard
                    title="Média Reprocessamento"
                    value={stats ? formatDuracao(Math.round(stats.avg_duracao_reprocessamento ?? 0)) : '-'}
                    sub="Reprocessamentos com sucesso"
                    icon={Timer}
                    color="#8b5cf6"
                    bgColor="#f5f3ff"
                />
            </div>

            {/* MONITOR AO VIVO — aparece automaticamente quando há job rodando */}
            <div style={{ marginBottom: '16px' }}>
                <LiveMonitor logs={logs} />
            </div>

            {/* FILTROS */}
            <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '20px 24px', marginBottom: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', alignItems: 'end' }}>
                    <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                            Processo
                        </label>
                        <select value={filtroProcesso} onChange={e => setFiltroProcesso(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                            <option value="">Todos</option>
                            {processosUnicos.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                            Status
                        </label>
                        <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                            <option value="">Todos</option>
                            <option value="sucesso">Sucesso</option>
                            <option value="parcial">Parcial</option>
                            <option value="erro">Erro</option>
                            <option value="rodando">Rodando</option>
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                            Período — De
                        </label>
                        <input type="date" value={filtroDataDe} onChange={e => setFiltroDataDe(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                            Período — Até
                        </label>
                        <input type="date" value={filtroDataAte} onChange={e => setFiltroDataAte(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button
                            onClick={limparFiltros}
                            disabled={!temFiltroAtivo}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: temFiltroAtivo ? '#fff1f2' : '#f8fafc', color: temFiltroAtivo ? themeRed : '#94a3b8', fontWeight: '700', fontSize: '13px', cursor: temFiltroAtivo ? 'pointer' : 'default', transition: 'all 0.2s' }}
                        >
                            Limpar Filtros
                        </button>
                    </div>
                </div>
            </div>

            {/* TABELA DE HISTÓRICO */}
            <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Activity size={18} color={themeBlue} />
                    <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Histórico de Execuções</h3>
                    <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#94a3b8' }}>
                        {temFiltroAtivo ? `${logsFiltrados.length} de ${logsArr.length}` : `${logsArr.length} registros`}
                    </span>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '900px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8fafc', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {[
                                    { field: 'data_inicio',      label: 'Código'        },
                                    { field: 'job_name',         label: 'Processo'      },
                                    { field: 'data_inicio',      label: 'Início'        },
                                    { field: 'duracao_segundos', label: 'Duração'       },
                                    { field: 'status',           label: 'Status'        },
                                    { field: 'tipo_execucao',    label: 'Tipo'          },
                                    { field: 'disparado_por',    label: 'Disparado por' },
                                ].map(({ field, label }) => (
                                    <th key={label} style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0' }}>
                                        <SortBtn field={field} label={label} />
                                    </th>
                                ))}
                                <th style={{ padding: '14px 20px', fontWeight: '600', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>Ação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: themeGray }}>
                                    <Loader size={20} className="animate-spin" style={{ display: 'inline', marginRight: '8px' }} /> Carregando...
                                </td></tr>
                            ) : logsVisiveis.length === 0 ? (
                                <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                                    {temFiltroAtivo ? 'Nenhum registro para os filtros selecionados.' : 'Nenhum registro encontrado.'}
                                </td></tr>
                            ) : logsVisiveis.map((log) => {
                                const isExpanded = expandedId === log.id;
                                const detalhes   = log.detalhes ? (typeof log.detalhes === 'string' ? JSON.parse(log.detalhes) : log.detalhes) : null;
                                // Usa hash do banco se disponível, fallback para cálculo local (registros antigos)
                                const codigo     = log.hash_execucao || gerarCodigo(log, logsArr);

                                return (
                                    <React.Fragment key={log.id}>
                                        <tr
                                            style={{ borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.15s' }}
                                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                                            onMouseOut={(e)  => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                        >
                                            {/* CÓDIGO + EXPAND */}
                                            <td style={{ padding: '14px 20px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <button
                                                        onClick={() => setExpandedId(isExpanded ? null : log.id)}
                                                        style={{ padding: '3px', borderRadius: '4px', border: '1px solid #e2e8f0', backgroundColor: 'transparent', cursor: 'pointer', color: themeGray, display: 'flex', alignItems: 'center', flexShrink: 0 }}
                                                    >
                                                        {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                                    </button>
                                                    <div>
                                                        <div style={{ fontFamily: 'monospace', fontWeight: '700', fontSize: '11px', color: '#334155' }}>#{codigo}</div>
                                                        {log.id_execucao_origem && (
                                                            <div style={{ fontSize: '10px', color: '#8b5cf6', marginTop: '2px' }}>↩ vinculado</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '14px 20px' }}>
                                                <span style={{ fontSize: '12px', fontWeight: '700', padding: '3px 10px', borderRadius: '12px', backgroundColor: '#eff6ff', color: themeBlue }}>
                                                    {log.job_name}
                                                </span>
                                            </td>
                                            <td style={{ padding: '14px 20px', color: '#334155', fontSize: '13px' }}>
                                                {formatData(log.data_inicio)}
                                            </td>
                                            <td style={{ padding: '14px 20px', color: themeGray, fontSize: '13px' }}>
                                                {formatDuracao(log.duracao_segundos)}
                                            </td>
                                            {/* STATUS — só badge */}
                                            <td style={{ padding: '14px 20px' }}>
                                                <StatusBadge status={log.status} />
                                            </td>
                                            <td style={{ padding: '14px 20px' }}>
                                                <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '12px', backgroundColor: log.tipo_execucao === 'reprocessamento' ? '#f5f3ff' : '#f1f5f9', color: log.tipo_execucao === 'reprocessamento' ? '#8b5cf6' : '#64748b' }}>
                                                    {log.tipo_execucao === 'reprocessamento' ? 'Reprocessamento' : 'Automático'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '14px 20px', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>
                                                {log.disparado_por || '—'}
                                            </td>
                                            <td style={{ padding: '14px 20px' }}>
                                                {podeReprocessar(log) ? (
                                                    <button
                                                        onClick={() => { setRunModal(log); setReprocessResult(null); }}
                                                        style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '6px', border: `1px solid ${themeBlue}`, backgroundColor: '#eff6ff', color: themeBlue, fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
                                                    >
                                                        <RotateCcw size={12} /> Reprocessar
                                                    </button>
                                                ) : foiReprocessado(log) ? (
                                                    <span title="Já reprocessado com sucesso" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc', color: '#94a3b8', fontSize: '12px', fontWeight: '700' }}>
                                                        <CheckCircle size={12} /> Reprocessado
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '600' }}>—</span>
                                                )}
                                            </td>
                                        </tr>

                                        {/* LINHA EXPANDIDA */}
                                        {isExpanded && (
                                            <tr style={{ backgroundColor: '#f8fafc' }}>
                                                <td colSpan="8" style={{ padding: '16px 24px 16px 52px', borderBottom: '1px solid #e2e8f0' }}>
                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', fontSize: '13px' }}>

                                                        {/* PERÍODO — sempre visível, vem dos campos diretos do log */}
                                                        <div>
                                                            <p style={{ fontWeight: '700', color: '#334155', margin: '0 0 6px 0', fontSize: '11px', textTransform: 'uppercase' }}>Período processado</p>
                                                            <p style={{ color: themeGray, fontWeight: '600', margin: 0 }}>
                                                                {formatPeriodo(log.periodo_inicio)}
                                                                {log.periodo_inicio !== log.periodo_fim && log.periodo_fim
                                                                    ? ` → ${formatPeriodo(log.periodo_fim)}`
                                                                    : ''}
                                                            </p>
                                                        </div>

                                                        {/* LOJAS — lojas_processadas, lojas (antiga), ou lojas_concluidas (interrompido) */}
                                                        {(detalhes?.lojas_processadas || detalhes?.lojas || detalhes?.lojas_concluidas) && (
                                                            <div>
                                                                <p style={{ fontWeight: '700', color: '#334155', margin: '0 0 6px 0', fontSize: '11px', textTransform: 'uppercase' }}>
                                                                    {detalhes?.interrupcao ? 'Lojas concluídas' : 'Lojas processadas'}
                                                                </p>
                                                                <p style={{ color: themeGray, margin: 0, fontSize: '12px' }}>
                                                                    {(detalhes.lojas_processadas || detalhes.lojas_concluidas || detalhes.lojas || []).join(', ')}
                                                                </p>
                                                            </div>
                                                        )}

                                                        {/* Sem detalhes — run anterior à implementação do log */}
                                                        {!detalhes && (
                                                            <div>
                                                                <p style={{ fontWeight: '700', color: '#334155', margin: '0 0 6px 0', fontSize: '11px', textTransform: 'uppercase' }}>Lojas processadas</p>
                                                                <p style={{ color: '#94a3b8', margin: 0, fontSize: '12px' }}>Sem detalhes (run anterior ao log)</p>
                                                            </div>
                                                        )}

                                                        {detalhes?.lojas_com_erro?.length > 0 && (
                                                            <div>
                                                                <p style={{ fontWeight: '700', color: themeRed, margin: '0 0 6px 0', fontSize: '11px', textTransform: 'uppercase' }}>Lojas com erro</p>
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                                                    {detalhes.lojas_com_erro.map(l => (
                                                                        <span key={l} style={{ padding: '2px 8px', borderRadius: '12px', backgroundColor: '#fff1f2', color: themeRed, fontWeight: '700', fontSize: '12px' }}>{l}</span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {log.mensagem_erro && (
                                                            <div>
                                                                <p style={{ fontWeight: '700', color: themeRed, margin: '0 0 6px 0', fontSize: '11px', textTransform: 'uppercase' }}>Mensagem de erro</p>
                                                                <p style={{ color: themeRed, margin: 0, fontSize: '12px' }}>{log.mensagem_erro}</p>
                                                            </div>
                                                        )}

                                                        {log.id_execucao_origem && (
                                                            <div>
                                                                <p style={{ fontWeight: '700', color: '#334155', margin: '0 0 6px 0', fontSize: '11px', textTransform: 'uppercase' }}>Run de origem</p>
                                                                <p style={{ color: '#8b5cf6', fontWeight: '700', margin: 0 }}>
                                                                    {gerarCodigo(logsArr.find(l => l.id === log.id_execucao_origem) || { id: log.id_execucao_origem }, logsArr)}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* PAGINAÇÃO */}
                {totalPaginas > 1 && (
                    <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '13px', color: themeGray }}>
                            Página {pagina} de {totalPaginas} · {logsFiltrados.length} registros
                        </span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <button onClick={() => setPagina(1)}  disabled={pagina === 1}          style={pgBtnStyle(pagina === 1)}>«</button>
                            <button onClick={() => setPagina(p => p - 1)} disabled={pagina === 1}  style={pgBtnStyle(pagina === 1)}>‹</button>
                            {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                                const p = Math.max(1, Math.min(pagina - 2, totalPaginas - 4)) + i;
                                return p <= totalPaginas ? (
                                    <button key={p} onClick={() => setPagina(p)} style={{ ...pgBtnStyle(false), backgroundColor: pagina === p ? themeBlue : '#fff', color: pagina === p ? '#fff' : '#334155', fontWeight: pagina === p ? '800' : '500' }}>
                                        {p}
                                    </button>
                                ) : null;
                            })}
                            <button onClick={() => setPagina(p => p + 1)} disabled={pagina === totalPaginas} style={pgBtnStyle(pagina === totalPaginas)}>›</button>
                            <button onClick={() => setPagina(totalPaginas)}  disabled={pagina === totalPaginas} style={pgBtnStyle(pagina === totalPaginas)}>»</button>
                        </div>
                    </div>
                )}
            </div>

            {/* MODAL */}
            {runModal && (
                <ModalReprocessar
                    run={runModal}
                    onClose={() => { setRunModal(null); setReprocessResult(null); }}
                    onConfirm={handleReprocessar}
                    loading={reprocessLoading}
                    resultado={reprocessResult}
                />
            )}
        </div>
    );
};

export default AdminMonitoramento;
